import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ExportMessagesParams,
  ExportMessagesQuery,
  ExportMessagesData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface ExportMessagesState
  extends MergeRequestState<ExportMessagesData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.exportMessages,
  isFetching: (state: ReduxState) => state.server.exportMessages.isFetching,
  data: (state: ReduxState) => state.server.exportMessages.data,
  error: (state: ReduxState) => state.server.exportMessages.error,
};

const initialState: ExportMessagesState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/exportMessages';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      query,
      config,
    }: {
      params: ExportMessagesParams;
      query: ExportMessagesQuery;
      config?: ServerApiConfig;
    },
    { rejectWithValue },
  ) => serverApi.exportMessages(params, query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerExportMessages {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const exportMessages: ServerExportMessages = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
